import { Button, EmptyState, LoadingState, Modal } from '@components/common';
import React, { useEffect, useMemo, useState } from 'react';
import { fetchItemSalesSummaryDetails } from '../../../services/reportsService';
import type {
  ItemSalesDetailMetric,
  ItemSalesDetailMetricCardProps,
  ItemSalesDetailsModalProps,
  ItemSalesDetailsTabButtonProps,
  ItemSalesModalTab,
} from '../../../types/reports';
import type {
  ItemSalesUserSummary,
  SalesItemView,
} from '../../../types/report';
import {
  formatReportDate,
  formatReportDateTime,
  formatReportMode,
  formatReportMoney,
  formatReportUserName,
} from '../../../util/reporting';

const buildRecordOrderLabel = (record: SalesItemView): string => {
  if (record.table_name) {
    return `${record.table_name} #${record.bill_number}`;
  }

  if (record.client_name) {
    return `${record.client_name} #${record.bill_number}`;
  }

  return `${formatReportMode(record.mode)} #${record.bill_number}`;
};

const DetailMetricCard: React.FC<ItemSalesDetailMetricCardProps> = ({
  label,
  value,
}) => (
  <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
    <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">
      {label}
    </p>
    <p className="mt-2 text-lg font-semibold text-slate-900">{value}</p>
  </div>
);

const TabButton: React.FC<ItemSalesDetailsTabButtonProps> = ({
  isActive,
  label,
  onClick,
}) => (
  <button
    type="button"
    onClick={onClick}
    className={`flex-1 border-b-2 px-4 py-4 text-center text-sm font-semibold uppercase tracking-[0.24em] transition ${
      isActive
        ? 'border-slate-900 text-slate-900'
        : 'border-transparent text-slate-400 hover:text-slate-600'
    }`}
  >
    {label}
  </button>
);

export const ItemSalesDetailsModal: React.FC<ItemSalesDetailsModalProps> = ({
  isOpen,
  summary,
  onClose,
}) => {
  const [activeTab, setActiveTab] = useState<ItemSalesModalTab>('overview');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sellerSummaries, setSellerSummaries] = useState<
    ItemSalesUserSummary[]
  >([]);
  const [records, setRecords] = useState<SalesItemView[]>([]);

  useEffect(() => {
    setActiveTab('overview');
  }, [summary?.id]);

  useEffect(() => {
    if (!isOpen || !summary) {
      setError(null);
      setSellerSummaries([]);
      setRecords([]);
      setIsLoading(false);
      return undefined;
    }

    let isMounted = true;

    const loadDetails = async () => {
      setIsLoading(true);
      setError(null);

      const response = await fetchItemSalesSummaryDetails(summary);

      if (!isMounted) return;

      if (!response.success || !response.data) {
        setError(response.error || 'Failed to load item sales details.');
        setSellerSummaries([]);
        setRecords([]);
        setIsLoading(false);
        return;
      }

      setSellerSummaries(response.data.data.sold_by);
      setRecords(response.data.data.records);
      setIsLoading(false);
    };

    loadDetails();

    return () => {
      isMounted = false;
    };
  }, [isOpen, summary]);

  const totalRecords = records.length;
  const summaryMetrics = useMemo<ItemSalesDetailMetric[]>(
    () =>
      summary
        ? [
            {
              label: 'Business day',
              value: formatReportDate(summary.sale_date),
            },
            {
              label: 'Unit price',
              value: formatReportMoney(summary.unit_price),
            },
            {
              label: 'Quantity sold',
              value: summary.quantity_sold.toLocaleString(),
            },
            {
              label: 'Gross sale',
              value: formatReportMoney(summary.gross_amount),
            },
            {
              label: 'Orders covered',
              value: summary.order_count.toLocaleString(),
            },
          ]
        : [],
    [summary],
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Item sales details"
      size="xl"
    >
      {!summary ? null : isLoading ? (
        <LoadingState
          size="md"
          message={`Loading ${summary.item_name.toLowerCase()} details...`}
        />
      ) : error ? (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error}
        </div>
      ) : (
        <div className="space-y-6">
          <div className="space-y-4">
            <div className="space-y-2">
              <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-emerald-600">
                Overview
              </p>
              <div className="flex flex-col gap-3 border-b border-slate-200 pb-4 md:flex-row md:items-end md:justify-between">
                <div>
                  <h4 className="text-xl font-semibold text-slate-900">
                    {summary.item_name}
                  </h4>
                  <p className="mt-1 text-sm text-slate-500">
                    {summary.outlet_name} · {summary.department_name}
                    {summary.category_name ? ` · ${summary.category_name}` : ''}
                  </p>
                </div>
                <div className="text-sm text-slate-500">
                  {summary.order_count.toLocaleString()} order
                  {summary.order_count === 1 ? '' : 's'} · {totalRecords} record
                  {totalRecords === 1 ? '' : 's'}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {summaryMetrics.map((metric) => (
                <DetailMetricCard key={metric.label} {...metric} />
              ))}
            </div>
          </div>

          <div className="flex border-b border-slate-200">
            <TabButton
              isActive={activeTab === 'overview'}
              label="Overview"
              onClick={() => setActiveTab('overview')}
            />
            <TabButton
              isActive={activeTab === 'records'}
              label="Bills / Orders"
              onClick={() => setActiveTab('records')}
            />
          </div>

          {activeTab === 'overview' ? (
            sellerSummaries.length === 0 ? (
              <EmptyState
                title="No sales details found"
                description="There are no sold records attached to this summary row."
              />
            ) : (
              <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
                <table className="w-full min-w-[720px] divide-y divide-slate-200">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                        #
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                        Employee
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                        Quantity sold
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                        Gross sale
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                        Records
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 bg-white">
                    {sellerSummaries.map((seller, index) => (
                      <tr
                        key={
                          seller.user_id ||
                          formatReportUserName(
                            seller.first_name,
                            seller.last_name,
                          )
                        }
                      >
                        <td className="px-4 py-4 text-sm text-slate-700">
                          {index + 1}
                        </td>
                        <td className="px-4 py-4 text-sm font-medium text-slate-900">
                          {formatReportUserName(
                            seller.first_name,
                            seller.last_name,
                          )}
                        </td>
                        <td className="px-4 py-4 text-right text-sm text-slate-700">
                          {seller.quantity_sold.toLocaleString()}
                        </td>
                        <td className="px-4 py-4 text-right text-sm text-slate-700">
                          {formatReportMoney(seller.gross_amount)}
                        </td>
                        <td className="px-4 py-4 text-right text-sm font-semibold text-slate-900">
                          {seller.record_count.toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-slate-50">
                    <tr>
                      <td
                        colSpan={2}
                        className="px-4 py-4 text-sm font-semibold text-slate-900"
                      >
                        Total items
                      </td>
                      <td className="px-4 py-4 text-right text-sm font-semibold text-slate-900">
                        {summary.quantity_sold.toLocaleString()}
                      </td>
                      <td className="px-4 py-4 text-right text-sm font-semibold text-slate-900">
                        {formatReportMoney(summary.gross_amount)}
                      </td>
                      <td className="px-4 py-4 text-right text-sm font-semibold text-slate-900">
                        {totalRecords.toLocaleString()}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </section>
            )
          ) : records.length === 0 ? (
            <EmptyState
              title="No bill records found"
              description="There are no bill or order lines available for this item summary."
            />
          ) : (
            <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
              <div className="max-h-[46vh] overflow-auto">
                <table className="w-full min-w-[1080px] divide-y divide-slate-200">
                  <thead className="sticky top-0 bg-slate-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                        Sold at
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                        Bill / Order
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                        Client
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                        Waiter
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                        Sold by
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                        Qty
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                        Gross
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                        Unit price
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 bg-white">
                    {records.map((record) => (
                      <tr key={record.id} className="align-top">
                        <td className="px-4 py-4 text-sm text-slate-700">
                          {formatReportDateTime(record.created_at)}
                        </td>
                        <td className="px-4 py-4 text-sm text-slate-900">
                          <div className="space-y-1">
                            <p className="font-medium">
                              {buildRecordOrderLabel(record)}
                            </p>
                            <p className="text-xs text-slate-500">
                              {formatReportMode(record.mode)}
                            </p>
                          </div>
                        </td>
                        <td className="px-4 py-4 text-sm text-slate-700">
                          {record.client_name || 'Walk-in'}
                        </td>
                        <td className="px-4 py-4 text-sm text-slate-700">
                          {record.waiter_name || 'Not assigned'}
                        </td>
                        <td className="px-4 py-4 text-sm text-slate-700">
                          {formatReportUserName(
                            record.added_by.first_name,
                            record.added_by.last_name,
                          )}
                        </td>
                        <td className="px-4 py-4 text-right text-sm text-slate-700">
                          {record.quantity.toLocaleString()}
                        </td>
                        <td className="px-4 py-4 text-right text-sm text-slate-700">
                          {formatReportMoney(record.total_amount)}
                        </td>
                        <td className="px-4 py-4 text-right text-sm text-slate-700">
                          {formatReportMoney(record.unit_price)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          <div className="flex justify-end">
            <Button
              variant="outline"
              onClick={onClose}
              className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
            >
              Close
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};
